//hook
import { UserConsumer } from '../hooks/context/UserContext';
import { NotificationConsumer } from '../hooks/context/NotificationContext';

//type
import { completedDriverReqType, driverProfileType } from '../api/completedRole/type/CompletedRoleType';

//api
import { PostCompletedRole } from '../api/completedRole/PostCompletedRole';
import { GetProfile } from '../api/profile/GetProfile';
import { UpdateCompletedRole } from '../api/completedRole/UpdateCompletedRole';

//formik and yap
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

//mui
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';

//translate
import { useTranslation } from 'react-i18next';
import i18n from '../translate/i18n';

interface driverFieldProps {
    setOpenCompletedProfile: (open: boolean) => void
    loading: boolean
    setLoading: (loading: boolean) => void
    RoleLicenseFront: string,
    setRoleLicenseFront: (RoleLicenseFront: string) => void
    RoleLicenseBack: string,
    setRoleLicenseBack: (RoleLicenseBack: string) => void
    driverProfile: driverProfileType
    setDriverProfile:(clientProfile: driverProfileType) => void
    driverPichture: string,
    setDriverPichture: (driverPichture: string) => void
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const storageURL = process.env.REACT_APP_STORAGE_BASE_URL;

const CompletedDriverField = (
    {
    setOpenCompletedProfile, 
    loading, 
    setLoading,
    RoleLicenseFront, 
    setRoleLicenseFront, 
    RoleLicenseBack, 
    setRoleLicenseBack, 
    driverProfile, 
    setDriverProfile,
    driverPichture, 
    setDriverPichture
    }: driverFieldProps
) => {
    const { t } = useTranslation();
    let {userData,setUserData, theme} = UserConsumer()
    let {setNoteMessage, setShowMessage, setStatusCode} = NotificationConsumer()
    
    //validation
    const driverSchema = Yup.object().shape({
        picture: Yup.mixed()
            .required(t("picture_required"))
            .test("fileType", t("invalid_file_type"), (value) => {
            if (!value) return false;
            if (typeof value === "string") return true;
            return ["image/jpeg", "image/png", "image/jpg"].includes((value as File).type);
        }),
        desired_wage: Yup.number()
        .required(t("desired_wage_required"))
        .test("desired_wage", t("the_number_must_be_greater_than_zero"), (value) => {
            if (!value) return false;
            return value > 0;
        }),
        price_per_hour: Yup.number()
        .required(t("price_per_hour_required"))
        .test("price_per_hour_required", t("the_number_must_be_greater_than_zero"), (value) => {
            if (!value) return false;
            return value > 0;
        }),
        extra_hour_price: Yup.number()
        .required(t("extra_hour_price_required"))
        .test("extra_hour_price_required", t("the_number_must_be_greater_than_zero"), (value) => {
            if (!value) return false;
            return value > 0;
        }),
        front_license_image_url: Yup.mixed()
            .required(t("front_image_required"))
            .test("fileType", t("invalid_file_type"), (value) => {
            if (!value) return false;
            if (typeof value === "string") return true;
            return ["image/jpeg", "image/png", "image/jpg"].includes((value as File).type);
        }),
        background_license_image_url: Yup.mixed()
            .required(t("background_image_required"))
            .test("fileType", t("invalid_file_type"), (value) => {
            if (!value) return false;
            if (typeof value === "string") return true;
            return ["image/jpeg", "image/png", "image/jpg"].includes((value as File).type);
        }),
    })

    //convert url to file
    const convertUrlToFile = async (url: string) => {
        const response = await fetch(`${storageURL}/${url}`);
        const blob = await response.blob();
        const ext = url.split('.').pop() || 'png';
        const fileName = url.split('/').pop()?.split('.').slice(0, -1).join('') || 'file';
        return new File([blob], `${fileName}.${ext}`, { type: blob.type });
    };

    //submit
    const handleSubmit = async (values: completedDriverReqType, theme:string)=> {
        try {
            setLoading(true)
            if ('picture' in values && typeof values.picture === "string") {
                values.picture = await convertUrlToFile(values.picture);
            }
            if ('front_license_image_url' in values && typeof values.front_license_image_url === "string") {
                values.front_license_image_url = await convertUrlToFile(values.front_license_image_url);
            }
            if ('background_license_image_url' in values && typeof values.background_license_image_url === "string") {
                values.background_license_image_url = await convertUrlToFile(values.background_license_image_url);
            }
            let result:any = ''
            if(userData?.is_driver) {
                result = await UpdateCompletedRole(values, theme, driverProfile?.id);
            }
            else {
                result = await PostCompletedRole(values, theme);
            }
            console.log(`completed driver successful:`);
            if(result.code == 200) {
                setNoteMessage(result.message)
                setShowMessage(true)
                setStatusCode(result.code)
                setRoleLicenseFront("")
                setRoleLicenseBack("")
                setDriverPichture("")
                setOpenCompletedProfile(false)
                const resultprofile = await GetProfile();
                setUserData(resultprofile.data);
                setDriverProfile({   
                    id: 0,
                    picture_url: null,
                    desired_wage: 0,
                    price_per_hour: 0,
                    extra_hour_price: 0,
                    front_license_image_url: null,
                    background_license_image_url: null
                })
            }
            else {
                setNoteMessage(result.message)
                setShowMessage(true)
                setStatusCode(result.code)
            }
        }
        catch (error:any) {
            console.error('completed failed', error);
            setNoteMessage(error.response.data.message)
            setShowMessage(true)
            setStatusCode(error.status)
        }
        finally {
            setLoading(false)
        }
    }

    return (
        <div>
            <Formik
            enableReinitialize
            initialValues={{
                picture: driverProfile?.picture_url ?? null,
                desired_wage: driverProfile?.desired_wage ?? null,
                price_per_hour: driverProfile?.price_per_hour ?? null,
                extra_hour_price: driverProfile?.extra_hour_price ?? null,
                front_license_image_url: driverProfile?.front_license_image_url ?? null,
                background_license_image_url: driverProfile?.background_license_image_url ?? null,
            }}
            validationSchema={driverSchema}
            onSubmit={(values)=>handleSubmit(values, theme)}
            >
            {({ values,touched, errors,setFieldValue }) => (
                <Form>
                    <div className="mb-5">
                        <div>
                            <Button
                                component="label"
                                className='!bg-paretnerColor !text-white w-fit !capitalize'
                                startIcon={<CloudUploadIcon />}
                            >
                                {t("profile_picture")}
                                <VisuallyHiddenInput
                                type="file"
                                accept="image/*"
                                onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    if (file) {
                                    setFieldValue("picture", file);
                                    setDriverPichture(URL.createObjectURL(file));
                                    }
                                }}
                                />
                            </Button>
                        </div>
                        {/* Show selected image preview */}
                        { driverPichture
                            ? (
                                <img
                                src={driverPichture}
                                alt="Profile picture preview"
                                className="mt-3 w-40 h-28 object-cover rounded-lg border"
                                />
                            ) 
                            : userData?.is_driver && driverProfile?.picture_url ? (
                                // Show existing uploaded image from backend
                                <img
                                src={`${storageURL}/${driverProfile?.picture_url}`}
                                alt="Profile picture preview"
                                className="mt-3 w-40 h-28 object-cover rounded-lg border"
                                />
                            ) : null
                        }
                        <p className="text-[12px] mt-2 text-red-500">
                                {touched.picture && errors.picture ? errors.picture : ""}
                        </p>
                    </div>
                    <div>
                        <Field
                            as={TextField}
                            name="desired_wage"
                            label={t("desired_wage")}
                            className="sm:w-1/2 w-full !mb-2"
                            helperText={touched.desired_wage && errors.desired_wage ? errors.desired_wage : ""}
                            error={touched.desired_wage && Boolean(errors.desired_wage)}
                        />
                        <Field
                            as={TextField}
                            type="number"
                            name="price_per_hour"
                            label={t("price_per_hour")}
                            className="sm:w-1/2 w-full !mb-2"
                            helperText={touched.price_per_hour && errors.price_per_hour ? errors.price_per_hour : ""}
                            error={touched.price_per_hour && Boolean(errors.price_per_hour)}
                        />
                        <Field
                            as={TextField}
                            type="number"
                            name="extra_hour_price"
                            label={t("extra_hour_price")}
                            className="sm:w-1/2 w-full !mb-2"
                            helperText={touched.extra_hour_price && errors.extra_hour_price ? errors.extra_hour_price : ""}
                            error={touched.extra_hour_price && Boolean(errors.extra_hour_price)}
                        />
                    </div>
                    <div className="mt-5 flex max-sm:flex-col max-sm:items-center gap-5">
                        <div>
                            <Button
                                component="label"
                                className='!bg-paretnerColor !text-white w-fit !capitalize'
                                startIcon={<CloudUploadIcon />}
                            >
                                {t("front_side")}
                                <VisuallyHiddenInput
                                type="file"
                                accept="image/*"
                                onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    if (file) {
                                    setFieldValue("front_license_image_url", file);
                                    setRoleLicenseFront(URL.createObjectURL(file));
                                    }
                                }}
                                />
                            </Button>
                            {/* Show selected image preview */}
                            {RoleLicenseFront
                                ? (
                                    <img
                                    src={RoleLicenseFront}
                                    alt="Front license preview"
                                    className="mt-3 w-40 h-28 object-cover rounded-lg border max-sm:m-auto"
                                    />
                                ) 
                                : userData?.is_driver && driverProfile?.front_license_image_url ? (
                                    // Show existing uploaded image from backend
                                    <img
                                    src={`${storageURL}/${driverProfile.front_license_image_url}`}
                                    alt="Front license uploaded"
                                    className="mt-3 w-40 h-28 object-cover rounded-lg border max-sm:m-auto"
                                    />
                                ) : null
                            }
                            <p className="text-[12px] mt-2 text-red-500">
                                {touched.front_license_image_url && errors.front_license_image_url ? errors.front_license_image_url : ""}
                            </p>
                        </div>
                        <div>
                            <Button
                                component="label"
                                className='!bg-paretnerColor !text-white w-fit !capitalize'
                                startIcon={<CloudUploadIcon />}
                            >
                                {t("back_side")}
                                <VisuallyHiddenInput
                                type="file"
                                accept="image/*"
                                onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    if (file) {
                                    setFieldValue("background_license_image_url", file);
                                    setRoleLicenseBack(URL.createObjectURL(file));
                                    }
                                }}
                                />
                            </Button>
                            {/* Show selected image preview */}
                            {RoleLicenseBack 
                                ? (
                                    <img
                                    src={RoleLicenseBack}
                                    alt="Back license preview"
                                    className="mt-3 w-40 h-28 object-cover rounded-lg border"
                                    />
                                ) 
                                : userData?.is_driver && driverProfile?.background_license_image_url ? (
                                    // Show existing uploaded image from backend
                                    <img
                                    src={`${storageURL}/${driverProfile.background_license_image_url}`}
                                    alt="Back license uploaded"
                                    className="mt-3 w-40 h-28 object-cover rounded-lg border"
                                    />
                                ) : null
                            }
                            <p className="text-[12px] mt-2 text-red-500">
                                {touched.background_license_image_url && errors.background_license_image_url ? errors.background_license_image_url : ""}
                            </p>
                        </div>
                    </div>
                    <hr className='mt-5'/>
                    <DialogActions className={i18n.language === "en" ? "!justify-start" : "!justify-start"}>
                        <Button className='w-full !bg-paretnerColor !text-white !rounded-full' type="submit">
                            {loading 
                            ? <CircularProgress className='w-1 h-1'/>
                            :t("confirm")
                            }
                        </Button>
                    </DialogActions>
                </Form>
            )}
            </Formik>
        </div>
    )
}

export default CompletedDriverField